import { KeyboardEvent, useContext, useEffect, useId, useRef, useState } from 'react'
import { runInAction } from 'mobx'
import styled from 'styled-components'

import { CriterionDto, GroupedCriteriaDto } from '../../../../api/generated'
import { colors } from '../../../../constants/colors'
import FilterStorage from '../../../../storage/FilterStorage/FilterStorage'
import { StyledButton } from '../../../projectComponents'
import { MiniModal } from '../../modals/MiniModal'

export const isStringChoiceCriterion = (criterion: CriterionDto) =>
  Boolean(criterion.id && criterion.possibleStringValues?.length) &&
  ['stringOneOf', 'stringExact', 'stringTerm'].includes(criterion.filterType) &&
  !/(цвет|color)/i.test(`${criterion.name ?? ''} ${criterion.propertyName ?? ''}`)

export function StringCriteriaModal({
  group,
  onClose,
}: {
  group: GroupedCriteriaDto
  onClose: () => void
}) {
  const store = useContext(FilterStorage)
  const titleId = useId()
  const returnFocus = useRef<HTMLElement | null>(null)
  const criteria = (group.criteria ?? []).filter(isStringChoiceCriterion)
  const [draft, setDraft] = useState<Record<number, string[]>>(() =>
    Object.fromEntries(
      criteria.map(criterion => {
        const id = criterion.id!
        const selected =
          criterion.filterType === 'stringOneOf'
            ? (store.sorting.stringOneOfFilters?.find(filter => filter.criterionId === id)
                ?.values ?? [])
            : [
                (criterion.filterType === 'stringExact'
                  ? store.sorting.stringExactFilters
                  : store.sorting.stringTermFilters
                )?.find(filter => filter.criterionId === id)?.value,
              ].filter(Boolean)
        return [id, selected.filter(value => criterion.possibleStringValues.includes(value))]
      })
    )
  )

  useEffect(
    () => () => {
      returnFocus.current?.focus()
    },
    []
  )

  const toggle = (criterion: CriterionDto, value: string) => {
    setDraft(previous => {
      const selected = previous[criterion.id!] ?? []
      const next = selected.includes(value)
        ? selected.filter(item => item !== value)
        : criterion.filterType === 'stringOneOf'
          ? [...selected, value]
          : [value]
      return { ...previous, [criterion.id!]: next }
    })
  }

  const apply = () => {
    runInAction(() => {
      criteria.forEach(criterion => {
        const selected = draft[criterion.id!] ?? []
        if (criterion.filterType === 'stringOneOf')
          store.setStringOneOfFilter(criterion.id!, selected)
        else if (criterion.filterType === 'stringExact' || criterion.filterType === 'stringTerm')
          store.setStringValueFilter(criterion.filterType, criterion.id!, selected[0])
      })
    })
    onClose()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      onClose()
    }
    if (event.key !== 'Tab') return
    const controls = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled)'
      )
    )
    const first = controls[0]
    const last = controls[controls.length - 1]
    if (
      event.shiftKey &&
      (document.activeElement === first || document.activeElement === event.currentTarget)
    ) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first?.focus()
    }
  }

  return (
    <Modal
      viewModal
      hideCloseButton
      setViewModal={open => {
        if (!open) onClose()
      }}
    >
      <Content
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        ref={node => {
          if (node && !returnFocus.current) {
            returnFocus.current = document.activeElement as HTMLElement
            node.focus()
          }
        }}
      >
        <Header>
          <Title id={titleId}>{group.groupName || 'Характеристики'}</Title>
          <Close type="button" onClick={onClose} aria-label="Закрыть">
            <img src="/images/buttons/filter-modal-close.svg" alt="" width={32} height={32} />
          </Close>
        </Header>
        <Sections>
          {criteria.map(criterion => (
            <Section key={criterion.id}>
              <SectionHeader>
                <Heading>{criterion.propertyName || criterion.name || 'Характеристика'}</Heading>
                <SectionActions>
                  <SectionAction
                    type="button"
                    disabled={
                      criterion.filterType !== 'stringOneOf' &&
                      criterion.possibleStringValues.length > 1
                    }
                    title={
                      criterion.filterType !== 'stringOneOf' &&
                      criterion.possibleStringValues.length > 1
                        ? 'Эта характеристика допускает только один вариант'
                        : undefined
                    }
                    onClick={() =>
                      setDraft(previous => ({
                        ...previous,
                        [criterion.id!]: [...new Set(criterion.possibleStringValues)],
                      }))
                    }
                  >
                    Все
                  </SectionAction>
                  <SectionAction
                    type="button"
                    onClick={() => setDraft(previous => ({ ...previous, [criterion.id!]: [] }))}
                  >
                    Сбросить
                  </SectionAction>
                </SectionActions>
              </SectionHeader>
              <Choices>
                {[...new Set(criterion.possibleStringValues)].map(value => (
                  <Choice key={value}>
                    <span>{value}</span>
                    <input
                      type="checkbox"
                      checked={(draft[criterion.id!] ?? []).includes(value)}
                      onChange={() => toggle(criterion, value)}
                    />
                  </Choice>
                ))}
              </Choices>
            </Section>
          ))}
        </Sections>
        <Apply type="button" onClick={apply}>
          Выбрать
        </Apply>
      </Content>
    </Modal>
  )
}

const Modal = styled(MiniModal)`
  width: min(579px, calc(100vw - 32px));
  height: min(568px, calc(100dvh - 32px));
  max-height: calc(100dvh - 32px);
  padding: 0;
  border-radius: 15px;
  overflow: hidden;
`

const Content = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  padding: 35px 36px 36px;
  display: flex;
  flex-direction: column;
  outline: none;
  @media (max-width: 449px) {
    padding: 24px 20px;
  }
`
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 30px;
  flex-shrink: 0;
`
const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 33px;
  color: ${colors.black};
  overflow-wrap: anywhere;
`
const Close = styled.button`
  display: flex;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  img {
    width: 32px;
    height: 32px;
  }
`
const Sections = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 16px;
`
const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const Heading = styled.h3`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  color: ${colors.black};
  min-width: 0;
  overflow-wrap: anywhere;
`
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
`
const SectionActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 16px;
  margin-left: auto;
`
const SectionAction = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${colors.primary};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`
const Choices = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`
const Choice = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 20px;
  box-sizing: border-box;
  max-width: 100%;
  padding: 9px 12px;
  border-radius: 7.556px;
  background: ${colors.lightGray};
  color: #394141;
  font-size: 14px;
  line-height: 1.1;
  font-weight: 600;
  cursor: pointer;
  span {
    overflow-wrap: anywhere;
    min-width: 0;
  }
  input {
    appearance: none;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    padding: 0;
    margin: 0;
    border: 0;
    border-radius: 4px;
    background: url('/images/buttons/filter-checkbox-inactive.svg') center / 20px 20px no-repeat;
    cursor: pointer;
  }
  input:checked {
    background: ${colors.primary} url('/images/buttons/filter-checkbox-checkmark.svg') center / 20px
      20px no-repeat;
  }
  input:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 3px;
  }
`
const Apply = styled(StyledButton)`
  flex-shrink: 0;
  align-self: center;
  width: min(358px, 100%);
  min-height: 50px;
  height: 50px;
  padding: 15px 24px;
  margin-top: 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
`
